/* eslint-disable camelcase */
import { useCallback, useMemo, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'

import { liveTurns, speakersById, toSpeakerIdentity } from './gtDerivations'
import { type GtDoc, PREFILL_ORIGIN_LABEL } from './gtModel'

import { isApiError } from '@/api'
import {
  getMeetingAttributionGtRaw,
  getMeetingAttributionLive,
  getMeetingAttributionObservations,
  getMeetingAttributionSpeakerMap,
  type PrefillOrigin,
} from '@/api/internal'
import {
  applySpeakerMappings,
  artifactEndMs,
  type AttributionSpan,
  deriveSpeakerMappings,
  deriveUnassignedPeople,
  entriesToRows,
  observationsToRows,
  parseObservationNdjson,
  parseRawTranscript,
  parseTranscriptNdjson,
  reconcileNamedSpeakerColors,
  rowsToSpans,
  type SpeakerMapping,
} from '@/pages/app/AttributionViewer/artifactDerivations'
import { refinedArtifactQueryOptions } from '@/pages/app/AttributionViewer/artifactQueries'
import { buildSpeakerStyleMap, type SpeakerStyleMap } from '@/pages/app/AttributionViewer/components/colors'
import { attributionViewerKeys } from '@/pages/app/AttributionViewer/queryKeys'

export type GtArtifactLaneState = 'loading' | 'present' | 'absent' | 'error'

export type GtArtifactLane = {
  id: 'refined' | 'raw' | 'gt'
  label: string
  state: GtArtifactLaneState
  spans: AttributionSpan[]
  onRetry?: () => void
}

type QueryState = { data: unknown; error: unknown; isError: boolean; isPending: boolean }

const artifactState = (query: QueryState): GtArtifactLaneState => {
  if (query.data !== undefined) return 'present'
  if (query.isPending) return 'loading'
  if (isApiError(query.error) && query.error.status === 404) return 'absent'
  return 'error'
}

const shouldRetryArtifact = (failureCount: number, error: unknown): boolean =>
  failureCount < 1 && (!isApiError(error) || error.status >= 500)

export const useGtArtifacts = (
  meetingId: string,
  doc: GtDoc,
  {
    prefillVersion,
    rawSha256,
    prefillOrigin,
  }: { prefillVersion: string; rawSha256: string; prefillOrigin: PrefillOrigin }
) => {
  const queryDefaults = {
    enabled: Boolean(meetingId),
    gcTime: 0,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    retry: shouldRetryArtifact,
    staleTime: Infinity,
  } as const

  const refinedQuery = useQuery({
    ...queryDefaults,
    ...refinedArtifactQueryOptions(meetingId),
  })
  const refinedBody = refinedQuery.data?.data
  const rawEnabled = Boolean(meetingId && prefillVersion && rawSha256)
  const rawQuery = useQuery({
    ...queryDefaults,
    enabled: rawEnabled,
    queryKey: attributionViewerKeys.annotationRaw(meetingId, prefillVersion, rawSha256),
    queryFn: ({ signal }) =>
      getMeetingAttributionGtRaw(meetingId, prefillVersion, rawSha256, signal).then((response) => response.data),
  })
  const liveQuery = useQuery({
    ...queryDefaults,
    queryKey: attributionViewerKeys.artifact(meetingId, 'live'),
    queryFn: ({ signal }) => getMeetingAttributionLive(meetingId, signal).then((response) => response.data),
  })
  const observationsQuery = useQuery({
    ...queryDefaults,
    queryKey: attributionViewerKeys.artifact(meetingId, 'observations'),
    queryFn: ({ signal }) => getMeetingAttributionObservations(meetingId, signal).then((response) => response.data),
  })
  const speakerMapQuery = useQuery({
    ...queryDefaults,
    queryKey: attributionViewerKeys.speakerMap(meetingId),
    queryFn: ({ signal }) => getMeetingAttributionSpeakerMap(meetingId, signal).then((response) => response.data),
  })

  const refinedArtifact = useMemo(() => parseTranscriptNdjson(refinedBody ?? ''), [refinedBody])
  const rawSourceLabel = PREFILL_ORIGIN_LABEL[prefillOrigin]
  const rawArtifact = useMemo(
    () => parseRawTranscript(rawQuery.data ?? '', rawSourceLabel),
    [rawQuery.data, rawSourceLabel]
  )
  const liveArtifact = useMemo(() => parseTranscriptNdjson(liveQuery.data ?? ''), [liveQuery.data])
  const observationsArtifact = useMemo(
    () => parseObservationNdjson(observationsQuery.data ?? ''),
    [observationsQuery.data]
  )
  const speakerMappings = useMemo<SpeakerMapping[]>(
    () => deriveSpeakerMappings(speakerMapQuery.data?.speaker_map),
    [speakerMapQuery.data?.speaker_map]
  )

  const refinedRows = useMemo(
    () => applySpeakerMappings(entriesToRows(refinedArtifact.entries, 'refined'), speakerMappings),
    [refinedArtifact.entries, speakerMappings]
  )
  const rawRows = rawArtifact.rows
  const liveRows = useMemo(() => entriesToRows(liveArtifact.entries, 'live'), [liveArtifact.entries])
  const observationRows = useMemo(
    () =>
      reconcileNamedSpeakerColors(
        applySpeakerMappings(observationsToRows(observationsArtifact), speakerMappings),
        refinedRows
      ),
    [observationsArtifact, refinedRows, speakerMappings]
  )

  const refinedState =
    refinedBody?.trim() && refinedArtifact.entries.length === 0 && refinedArtifact.invalidLineCount > 0
      ? 'error'
      : artifactState(refinedQuery)
  const rawState = !rawEnabled
    ? 'absent'
    : rawQuery.data !== undefined && !rawArtifact.usable
      ? 'error'
      : artifactState(rawQuery)

  const refinedQueryRef = useRef(refinedQuery)
  refinedQueryRef.current = refinedQuery
  const rawQueryRef = useRef(rawQuery)
  rawQueryRef.current = rawQuery
  const speakerMapQueryRef = useRef(speakerMapQuery)
  speakerMapQueryRef.current = speakerMapQuery
  const speakerMapState =
    speakerMapQuery.data !== undefined && speakerMapQuery.data.speaker_map == null
      ? 'absent'
      : artifactState(speakerMapQuery)

  const gtSpans = useMemo<AttributionSpan[]>(() => {
    const bySpeakerId = speakersById(doc.speakers)
    return [...liveTurns(doc)]
      .sort((a, b) => a.start_ms - b.start_ms)
      .map((turn) => {
        const speaker = turn.speaker_id ? (bySpeakerId.get(turn.speaker_id) ?? null) : null
        return {
          start_ms: turn.start_ms,
          end_ms: turn.end_ms,
          speaker: speaker ? toSpeakerIdentity(speaker, speakerMappings) : null,
        }
      })
  }, [doc, speakerMappings])

  const lanes = useMemo<GtArtifactLane[]>(
    () => [
      {
        id: 'refined',
        label: 'Refined',
        state: refinedState,
        spans: rowsToSpans(refinedRows),
        onRetry: () => void refinedQueryRef.current.refetch(),
      },
      {
        id: 'raw',
        label: `${rawSourceLabel} clusters`,
        state: rawState,
        spans: rowsToSpans(rawRows),
        onRetry: () => void rawQueryRef.current.refetch(),
      },
      { id: 'gt', label: 'GT (this review)', state: 'present', spans: gtSpans },
    ],
    [refinedState, refinedRows, rawSourceLabel, rawState, rawRows, gtSpans]
  )

  const endMs = useMemo(
    () =>
      Math.max(
        artifactEndMs([{ rows: refinedRows }, { rows: rawRows }, { rows: liveRows }, { rows: observationRows }]),
        ...gtSpans.map((span) => span.end_ms)
      ),
    [refinedRows, rawRows, liveRows, observationRows, gtSpans]
  )

  const speakerStyles = useMemo<SpeakerStyleMap>(
    () =>
      buildSpeakerStyleMap([
        ...lanes.flatMap((lane) => lane.spans.flatMap((span) => (span.speaker ? [span.speaker] : []))),
        ...doc.speakers.map((speaker) => toSpeakerIdentity(speaker, speakerMappings)),
        ...speakerMappings.map((mapping) => ({
          key: `mapping:${mapping.speakerId}`,
          colorKey: mapping.colorKey,
          label: `Mapping · refined · ${mapping.name ?? `Speaker ${mapping.speakerId}`}`,
          name: mapping.name,
          speakerId: mapping.speakerId,
        })),
      ]),
    [lanes, speakerMappings, doc.speakers]
  )

  const unassignedPeople = useMemo(
    () => deriveUnassignedPeople(speakerMapQuery.data?.speaker_map, speakerMappings),
    [speakerMapQuery.data?.speaker_map, speakerMappings]
  )

  const hasObservations = artifactState(observationsQuery) === 'present'
  const observationPlatform = observationsArtifact.platform
  const observationOs = observationsArtifact.os
  const onRetrySpeakerMap = useCallback(() => void speakerMapQueryRef.current.refetch(), [])

  return useMemo(
    () => ({
      lanes,
      endMs,
      speakerStyles,
      speakerMappings,
      speakerMapState,
      unassignedPeople,
      hasObservations,
      observationPlatform,
      observationOs,
      onRetrySpeakerMap,
    }),
    [
      lanes,
      endMs,
      speakerStyles,
      speakerMappings,
      speakerMapState,
      unassignedPeople,
      hasObservations,
      observationPlatform,
      observationOs,
      onRetrySpeakerMap,
    ]
  )
}
